import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button} from '@tickchak/react-share/app';
import type {UserChatMessage} from '../../../aiMessageGenerator/utils/types';
import * as css from './AiAgentUserMessage.css';
import AiAgentFileIcon from '../../../../assets/icons/AiAgentFileIcon';
import AiAgentPencilIcon from '../../../../assets/icons/AiAgentPencilIcon';
import AiAgentUserMessageEdit from './AiAgentUserMessageEdit';

interface AiAgentUserMessageProps {
    message: UserChatMessage;
    index: number;
    onEditMessage?: (index: number, newText: string) => void;
    draftLoading?: boolean;
}

function AiAgentUserMessage({message, index, onEditMessage, draftLoading}: AiAgentUserMessageProps) {
    const {t} = useTranslation('aiAgent');
    const {attachment, text} = message;
    const [isEditing, setIsEditing] = useState(false);
    const canEdit = Boolean(text?.trim()) && Boolean(onEditMessage) && !draftLoading;

    const handleEditClick = useCallback(() => setIsEditing(true), []);
    const handleCancel = useCallback(() => setIsEditing(false), []);
    const handleSave = useCallback((newText: string) => {
        onEditMessage?.(index, newText);
        setIsEditing(false);
    }, [index, onEditMessage]);

    return (
        <css.container data-cy={`ai-agent-user-message-${index}`}>
            {attachment?.kind === 'image' && (
                <css.imageAttachment src={attachment.previewUrl} alt={attachment.name} />
            )}
            {attachment?.kind === 'file' && (
                <css.fileAttachment>
                    <css.fileName>{attachment.name}</css.fileName>
                    <AiAgentFileIcon />
                </css.fileAttachment>
            )}
            {text && (
                <css.messageRow $isEditing={isEditing}>
                    {isEditing ? (
                        <AiAgentUserMessageEdit
                            initialText={text}
                            index={index}
                            onSave={handleSave}
                            onCancel={handleCancel}
                        />
                    ) : (
                        <>
                            {canEdit && (
                                <css.editButtonWrap>
                                    <Button
                                        variant="primary"
                                        size="category"
                                        iconStart={<AiAgentPencilIcon />}
                                        ariaLabel={t('editMessage')}
                                        onClick={handleEditClick}
                                        data-cy={`ai-agent-user-message-edit-${index}`}
                                    />
                                </css.editButtonWrap>
                            )}
                            <css.textBubble>{text}</css.textBubble>
                        </>
                    )}
                </css.messageRow>
            )}
        </css.container>
    );
}

export default AiAgentUserMessage;
